import { BigNumber } from '@ethersproject/bignumber';
import { parseUnits } from '@ethersproject/units';
import { BIG_ZERO } from 'app/constants';
import { sum } from '../big-number/calculations';
import { shiftDecimal as newShiftDecimals } from '@silo/utils';
import { BigNumberWithDecimals, PERCENT_DECIMALS } from '@silo/types';

const NEGATIVE_ONE = BigNumber.from('-1');
const ZERO = BigNumber.from('0');
const ONE = BigNumber.from('1');
const TWO = BigNumber.from('2');

export type RoundingType = 'half-up' | 'up' | 'down';

/**
 * @deprecated Use `BigIntWithDecimals` version instead
 *
 * Shifts decimals on an integer. Decimals can be positive or negative.
 *
 * ```
 * shiftDecimal(BigNumber.from(1_000), 2) // => 100,000 (i.e. 1,000 x 10^2)
 * shiftDecimal(BigNumber.from(1_000), -2) // => 10 (i.e. 1,000 x 10^-2)
 * ```
 *
 * For negative decimals, remaining fractions are rounded to the closest integer.
 *
 * ```
 * shiftDecimal(BigNumber.from(499_999), -2) // => 5,000
 * ```
 */
export const shiftDecimal = (value: BigNumber, decimals: number, roundingType: RoundingType = 'half-up') => {
  if (!Number.isInteger(decimals)) {
    throw new Error(`Must pass an integer for the decimals argument. Value received: ${decimals}`);
  }

  if (decimals === 0) return value;

  if (decimals > 0) {
    return value.mul(parseUnits('1', decimals) as BigNumber);
  }

  const by = parseUnits('1', Math.abs(decimals)) as BigNumber;
  const signFactor = value.lt(ZERO) ? NEGATIVE_ONE : ONE;
  const abs = value.abs();

  const toValue = abs.div(by);
  const mod = abs.mod(by);

  const shouldRoundUp = () => {
    if (mod.isZero()) return false;

    switch (roundingType) {
      case 'half-up':
      default:
        return mod.gte(by.div(TWO));
      case 'down':
        return false;
      case 'up':
        return true;
    }
  };

  const rounded = shouldRoundUp() ? toValue.add(ONE) : toValue;

  return rounded.mul(signFactor);
};

/**
 * @deprecated Use `BigIntWithDecimals` version instead
 *
 * Converts value from currency A to currency B given:
 *
 * amount: atomic units of currency A
 * price: atomic units of currency B per unit of A
 */

export const convertCurrency = (
  amount: BigNumberWithDecimals,
  price: BigNumberWithDecimals
): BigNumberWithDecimals => ({
  decimals: price.decimals,
  bigNumber: shiftDecimal(amount.bigNumber.mul(price.bigNumber), -amount.decimals),
});

/**
 * Sums an array of values that can be either a `BigNumber` or `null`.
 *
 * `null` values are treated as zero.
 */
export const sumNullableNumbers = (values: (BigNumber | null)[]) => sum(values.map((value) => value || BIG_ZERO));

export const calculateUtilization = (totalDeposits: bigint, borrows: bigint) => {
  if (totalDeposits === 0n) return 0n;

  return newShiftDecimals(borrows, PERCENT_DECIMALS) / totalDeposits;
};
